import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import useIsMounted from "../../../hooks/use-is-mounted";
import {Box, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import Button from "../../base/button/button";
import {CreateWarehouseForOrderItemsDataGridColumnNames, issueCreditFromInvoiceItemSelectionDataGridColumnNames} from "../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../packages/data-grid";
import PartnerDataGrid from "../../../containers/data-grid";
import Api from "../../../../core/services/api/api";

const dataGridSaveConfig = {
    version: 1,
    name: 'CreateMissingItemsInWarehousesDialogDataGrid',
}


const CreateMissingItemsInWarehousesDialog = ({items, open, close, warehouses}) => {
    const [creating, setCreating] = useState([]);
    const [createdFor, setCreatedFor] = useState([])
    const isMounted = useIsMounted();

    /**
     * After dialog is opened, reset the creating and createdFor states
     */
    useLayoutEffect(() => {
        if (open) {
            setCreating([]);
            setCreatedFor([]);
        }
    }, [open])

    /**
     * Close the dialog if all items are created.
     */
    useEffect(() => {
        if (open) {
            if (createdFor.length === items.length) {
                close();
            }
        }
    }, [close, createdFor.length, items.length, open]);


    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Part No",
                name: CreateWarehouseForOrderItemsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {type: DataGridColumnWidthTypes.flex, size: 1},
            },
            {
                title: "Warehouse",
                name: CreateWarehouseForOrderItemsDataGridColumnNames.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {type: DataGridColumnWidthTypes.flex, size: 1},
            },
            {
                name: CreateWarehouseForOrderItemsDataGridColumnNames.action,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: {type: DataGridColumnWidthTypes.flex, size: 1},
            },
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**
     * Create warehouse for order items
     * @type {(function(*): Promise<*>)}
     */
    const createWarehouseForOrderItems = useCallback(async (selectedItems) => {
        setCreating(prevState => [...prevState, ...selectedItems.map(e => e.id)]);
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: selectedItems.map(e => e.partNo),
        });
        if (!isMounted())
            return;
        if (!partInfoResponse?.resultFlag) {
            setCreating(prevState => prevState.filter(e => !selectedItems.map(e => e.id).includes(e)));
            return;
        }
        const forApis = selectedItems.map(item => {
            const warehouse = warehouses.find(e => e.code === item.whse);
            return {
                itemId: item.id,
                partId: partInfoResponse.data?.find(e => e.partNo === item.partNo)?.id,
                warehouseId: warehouse?.id,
                warehouseCode: warehouse?.code,
                isMainWarehouse: warehouse?.isMain ?? warehouse?.code === '00',
            }
        })
        const hasMainWarehouseInApis = forApis.some(e => e.isMainWarehouse);
        if (hasMainWarehouseInApis) {
            forApis.sort((a, b) => {
                if (a.isMainWarehouse && !b.isMainWarehouse) {
                    return -1;
                } else if (!a.isMainWarehouse && b.isMainWarehouse) {
                    return 1;
                } else {
                    return 0;
                }
            });
        }
        for (const forApi of forApis) {
            const response = await Api.createPartInWarehouse({
                partId: forApi.partId,
                warehouseId: forApi.warehouseId,
            });
            if (!isMounted())
                return;
            setCreating(prevState => prevState.filter(e => forApi.itemId !== e));
            if (response?.resultFlag) {
                setCreatedFor(prevState => [...prevState, forApi.itemId]);
            }
        }
    }, [isMounted, warehouses])

    const dataGridRows = useMemo(() => items.map(item => {
        const warehouse = warehouses.find(e => e.code === item.whse);
        return {
            key: item.id,
            data: item,
            cells: {
                [CreateWarehouseForOrderItemsDataGridColumnNames.partNo]: item.partNo,
                [CreateWarehouseForOrderItemsDataGridColumnNames.warehouse]: !!warehouse ? `${warehouse.name} (${warehouse.code})` : 'N/A',
                [CreateWarehouseForOrderItemsDataGridColumnNames.action]: (
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={creating.includes(item.id)}
                        onClick={() => createWarehouseForOrderItems([item])}
                    >
                        {
                            creating.includes(item.id) ? 'Creating...' : 'Create'
                        }
                    </Button>
                )
            }
        }
    }), [createWarehouseForOrderItems, creating, items, warehouses]);

    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth={'sm'}
            scroll={'paper'}
            aria-labelledby="scroll-dialog-title"
        >
            <DialogTitle>
                Create Missing Items in Warehouse
            </DialogTitle>
            <DialogContent dividers>
                <div className={'w-100 pb-4 d-flex flex-column'}>
                    <Typography variant={'body2'} className={'mb-3'}>
                        To proceed, we need to ensure that the following items exist in their selected warehouses. Please review the list below and click on the "Create" button to create the warehouse
                        for the respective item.
                        You may alternatively, choose to remove these item or change their warehouses.
                    </Typography>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        columns={dataGridColumns}
                        initialLayoutState={{columns: columns}}
                        hideFooter
                        classNames={{
                            container: "px-0",
                        }}
                        hideExporting
                        visibleRows={10}
                        rows={dataGridRows}
                        state={{
                            density: dataGridDensity,
                            pagination: {currentPage: 1, pageSize: items.length},
                            group: {
                                columnName: issueCreditFromInvoiceItemSelectionDataGridColumnNames.groupBy,
                                showOnColumn: issueCreditFromInvoiceItemSelectionDataGridColumnNames.partNo,
                            },
                        }}
                    />
                </div>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{marginLeft: '0 !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        borderRadius={'8px 0 8px 0'}
                        disabled={!!creating.length}
                        onClick={() => createWarehouseForOrderItems(items)}
                    >
                        {
                            creating.length ? 'Creating...' : 'Create for All'
                        }
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default CreateMissingItemsInWarehousesDialog;